/**
 * Shared invoice line table used by draft edit and work-order billing (#1680).
 * Controlled: the parent owns `lines`. Group metadata passes through unchanged.
 */

import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { sanitizeDecimalInput } from '@/utils/money';
import {
  emptyEditorLine,
  editorLineGrossCents,
  INVOICE_LINE_TYPES,
  INVOICE_LINE_UNITS,
  type InvoiceEditorLine,
} from './invoiceEditorLines';


interface InvoiceLinesEditorProps {
  lines: InvoiceEditorLine[];
  onChange: (lines: InvoiceEditorLine[]) => void;
  readOnly?: boolean;
  fallbackTaxPercent: number;
}

export function InvoiceLinesEditor({
  lines,
  onChange,
  readOnly = false,
  fallbackTaxPercent,
}: InvoiceLinesEditorProps) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');

  const updateLine = (key: string, patch: Partial<InvoiceEditorLine>) => {
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };
  const removeLine = (key: string) => {
    onChange(lines.filter((line) => line.key !== key));
  };
  const addLine = () => {
    onChange([...lines, emptyEditorLine(fallbackTaxPercent, `new-${Date.now()}`)]);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('edit.linesTitle')}
        </Typography>
        {!readOnly && (
          <Button size="small" startIcon={<AddIcon />} onClick={addLine}>
            {t('edit.addLine')}
          </Button>
        )}
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 110 }}>{t('edit.lineType')}</TableCell>
              <TableCell sx={{ minWidth: 160 }}>{t('print.description')}</TableCell>
              <TableCell align="right" sx={{ width: 80 }}>{t('print.quantity')}</TableCell>
              <TableCell sx={{ width: 90 }}>{t('print.unit')}</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>{t('edit.unitPriceInclVat')}</TableCell>
              <TableCell align="right" sx={{ width: 70 }}>{t('print.lineDiscountHeader')}</TableCell>
              <TableCell align="right" sx={{ width: 70 }}>{t('print.lineVat')}</TableCell>
              <TableCell align="center" sx={{ width: 44 }}>{t('edit.taxable')}</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>{t('edit.lineTotalInclVat')}</TableCell>
              {!readOnly && <TableCell padding="none" sx={{ width: 40 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.key} data-testid="edit-line-row">
                <TableCell>
                  <TextField
                    select
                    variant="standard"
                    fullWidth
                    value={line.lineType}
                    disabled={readOnly}
                    onChange={(e) => updateLine(line.key, {
                      lineType: e.target.value as InvoiceEditorLine['lineType'],
                    })}
                  >
                    {INVOICE_LINE_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>{t(`edit.lineTypes.${type}`)}</MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    variant="standard"
                    fullWidth
                    value={line.description}
                    disabled={readOnly}
                    onChange={(e) => updateLine(line.key, { description: e.target.value })}
                    placeholder={t('edit.descriptionPlaceholder')}
                    slotProps={{ htmlInput: { 'aria-label': t('print.description') } }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    variant="standard"
                    value={line.quantity}
                    disabled={readOnly}
                    onChange={(e) => updateLine(line.key, { quantity: sanitizeDecimalInput(e.target.value) })}
                    slotProps={{ htmlInput: { inputMode: 'decimal', style: { textAlign: 'right' }, 'aria-label': t('print.quantity') } }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    variant="standard"
                    fullWidth
                    value={line.unit}
                    disabled={readOnly}
                    onChange={(e) => updateLine(line.key, { unit: e.target.value })}
                  >
                    {INVOICE_LINE_UNITS.map((unit) => (
                      <MenuItem key={unit} value={unit}>{tc(`units.${unit}`)}</MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    variant="standard"
                    value={line.unitPriceEuros}
                    disabled={readOnly}
                    onChange={(e) => updateLine(line.key, {
                      unitPriceEuros: sanitizeDecimalInput(e.target.value),
                    })}
                    slotProps={{ htmlInput: { inputMode: 'decimal', style: { textAlign: 'right' }, 'aria-label': t('edit.unitPriceInclVat') } }}
                  />
                </TableCell>
                <TableCell align="right">
                  {/* Read-only (#1816): the discount is now set on the work order
                      (parts and labor items both), so re-editing it here at
                      invoicing time would just diverge from that source. */}
                  <Tooltip title={t('edit.discountFromWorkOrderTooltip')}>
                    {/* Disabled inputs don't receive pointer events — wrap in a
                        span so the tooltip still shows on hover (MUI FAQ). */}
                    <span>
                      <TextField
                        variant="standard"
                        value={line.discountPercent}
                        disabled
                        fullWidth
                        slotProps={{ htmlInput: { inputMode: 'decimal', style: { textAlign: 'right' } } }}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    variant="standard"
                    value={line.taxPercent}
                    disabled={readOnly || !line.taxable}
                    onChange={(e) => updateLine(line.key, {
                      taxPercent: sanitizeDecimalInput(e.target.value),
                    })}
                    slotProps={{ htmlInput: { inputMode: 'decimal', style: { textAlign: 'right' } } }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    size="small"
                    checked={line.taxable}
                    disabled={readOnly}
                    onChange={(e) => updateLine(line.key, { taxable: e.target.checked })}
                    inputProps={{ 'aria-label': t('edit.taxable') }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{formatPrice(editorLineGrossCents(line))}</Typography>
                </TableCell>
                {!readOnly && (
                  <TableCell padding="none">
                    <Tooltip title={t('edit.removeLine')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => removeLine(line.key)}
                          disabled={lines.length <= 1}
                          aria-label={t('edit.removeLine')}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
